import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, X, Sparkle, Moon, ClockCounterClockwise } from "@phosphor-icons/react";
import usePushNotifications from "../hooks/usePushNotifications";

/**
 * Auto-appearing modal that asks installed-PWA users (iOS + Android) to
 * enable Web Push. The landing-page banner sits below the fold and iPhone
 * users — who typically open the app full-screen from the Home Screen —
 * never scroll to it. This modal fixes that gap.
 *
 * Trigger rules:
 *  - App is in standalone (installed-PWA) mode
 *  - Push is supported on this runtime
 *  - User isn't already subscribed and hasn't denied permission
 *  - User hasn't dismissed within the last 7 days
 *  - Delay 1.5s on first paint so we don't blast them before the UI settles
 */
const DISMISS_KEY = "akc_push_modal_dismissed_at_v1";
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export default function PushPermissionModal() {
  const push = usePushNotifications();
  const [open, setOpen] = useState(false);
  const [enabling, setEnabling] = useState(false);

  useEffect(() => {
    if (!push.supported) return;
    if (!push.isStandalone) return;        // Only auto-prompt installed PWA users
    if (push.subscribed) return;
    if (push.permission === "denied") return;

    let dismissedAt = 0;
    try { dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10); } catch (e) { dismissedAt = 0; }
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_COOLDOWN_MS) return;

    const t = setTimeout(() => setOpen(true), 1500);
    return () => clearTimeout(t);
  }, [push.supported, push.isStandalone, push.subscribed, push.permission]);

  const handleDismiss = () => {
    setOpen(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (e) { /* storage may be blocked */ }
  };

  const handleEnable = async () => {
    setEnabling(true);
    const result = await push.enable();
    setEnabling(false);
    if (result.ok) {
      toast.success("Notifications enabled. Tomorrow looks better. 🔔");
      setOpen(false);
    } else if (result.error === "permission_denied") {
      toast.error("Permission blocked. Enable it from iPhone Settings → AstroKuberChat → Notifications.");
      handleDismiss();
    } else {
      toast.error("Couldn't enable. Please try again.");
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 sm:p-6 bg-black/55 backdrop-blur-sm animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="push-modal-title"
      data-testid="push-permission-modal"
    >
      <div
        className="relative w-full max-w-md rounded-3xl bg-[#FDF7EA] border border-amber-200 shadow-2xl overflow-hidden animate-in slide-in-from-bottom-6 duration-300"
      >
        {/* Decorative top gradient with bell */}
        <div className="relative h-32 bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <Sparkle size={28} weight="fill" className="absolute top-4 left-6 text-white" />
            <Moon size={22} weight="fill" className="absolute top-10 right-8 text-white" />
            <Sparkle size={16} weight="fill" className="absolute bottom-4 left-1/3 text-white" />
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-16 w-16 rounded-2xl bg-white/95 flex items-center justify-center shadow-xl ring-4 ring-white/30">
              <Bell size={30} weight="fill" className="text-orange-600" />
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 p-1.5 rounded-full bg-white/20 hover:bg-white/30 text-white"
            aria-label="Dismiss"
            data-testid="push-modal-close"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pt-5 pb-6">
          <h2
            id="push-modal-title"
            className="font-serif-display text-2xl text-[#3F2D1B] text-center leading-snug"
          >
            Never miss your auspicious moment
          </h2>
          <p className="text-sm text-stone-700 text-center mt-2 leading-relaxed">
            Quiet, thoughtful nudges — only when the stars truly align for you.
          </p>

          {/* Three benefits */}
          <ul className="mt-5 space-y-3">
            <Benefit
              icon={<ClockCounterClockwise size={18} weight="duotone" className="text-amber-700" />}
              title="Mahurat windows"
              desc="Wealth, marriage & travel timings — sent 30 min before they open."
            />
            <Benefit
              icon={<Moon size={18} weight="duotone" className="text-amber-700" />}
              title="Planetary alerts for your kundali"
              desc="Mars retrograde, Rahu Kaal & sign-changes personalised to your chart."
            />
            <Benefit
              icon={<Sparkle size={18} weight="duotone" className="text-amber-700" />}
              title="Free live sessions"
              desc="Limited-seat group readings with our top astrologers."
            />
          </ul>

          {/* Actions */}
          <div className="mt-6 space-y-2">
            <button
              onClick={handleEnable}
              disabled={enabling}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 px-6 py-3 text-sm font-semibold text-white shadow-md hover:opacity-95 disabled:opacity-60"
              data-testid="push-modal-enable"
            >
              <Bell size={15} weight="fill" />
              {enabling ? "Enabling…" : "Enable notifications"}
            </button>
            <button
              onClick={handleDismiss}
              className="w-full text-xs text-stone-600 hover:text-stone-900 py-2"
              data-testid="push-modal-later"
            >
              Maybe later
            </button>
          </div>

          <div className="mt-3 text-[10px] text-center text-stone-500 leading-relaxed">
            iPhone will show its own permission prompt next. Tap <span className="font-medium">Allow</span> to finish.
          </div>
        </div>
      </div>
    </div>
  );
}

function Benefit({ icon, title, desc }) {
  return (
    <li className="flex items-start gap-3">
      <div className="flex-none mt-0.5 h-8 w-8 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center">
        {icon}
      </div>
      <div className="flex-1">
        <div className="text-sm font-semibold text-[#3F2D1B]">{title}</div>
        <div className="text-xs text-stone-700 leading-snug">{desc}</div>
      </div>
    </li>
  );
}
